import { useState, useEffect } from 'react';
import { DocumentProcessor } from '../utils/documentProcessor';
import type { Flag } from '../lib/types';
import { motion } from 'framer-motion';
import { Info, AlertCircle, Shield, Loader2, AlertTriangle } from 'lucide-react';

interface ModerationContextProps {
  text: string;
  flags: Flag[];
  overallToxicity: number;
}

interface SimilarModeration {
  content: string;
  flags: Flag[];
  overall_toxicity: number;
  similarity: number;
}

export function ModerationContext({ text, flags, overallToxicity }: ModerationContextProps) {
  const [similarModerations, setSimilarModerations] = useState<SimilarModeration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadContext() {
      try {
        setError(null);
        setIsLoading(true);
        
        // Initialize the RAG system if not already initialized
        await DocumentProcessor.initialize();
        
        if (!mounted) return;

        // First check if this content already exists in our database
        const similarResults = await DocumentProcessor.searchSimilarModerations(text);
        
        // Only store if the content doesn't already exist with high similarity
        const contentAlreadyExists = similarResults.some((result: SimilarModeration) => 
          result.content === text || result.similarity > 0.95
        );
        
        if (!contentAlreadyExists) {
          // Store the current moderation result in Supabase
          await DocumentProcessor.storeModerationResult({
            content: text,
            flags,
            overallToxicity,
            metadata: {
              timestamp: new Date().toISOString(),
              type: 'moderation_result'
            }
          });
        }
        
        if (!mounted) return;

        if (mounted) {
          setSimilarModerations(similarResults);
        }
      } catch (error) {
        console.error('Error loading moderation context:', error);
        if (mounted) {
          setError(error instanceof Error ? error.message : 'Failed to load moderation context');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadContext();

    return () => {
      mounted = false;
    };
  }, [flags, text, overallToxicity]);

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 rounded-xl">
        <div className="flex items-center gap-2 text-red-400">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 bg-white/5 rounded-xl">
        <div className="flex items-center gap-2 text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading similar cases...</span>
        </div>
      </div>
    );
  }

  if (similarModerations.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center gap-2 text-neutral-400">
        <Info className="w-4 h-4" />
        <span>Similar Past Cases</span>
      </div>

      {similarModerations.map((moderation, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="p-4 bg-white/5 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <div className="mt-1">
              {moderation.overall_toxicity > 0.7 ? (
                <AlertCircle className="w-4 h-4 text-red-400" />
              ) : (
                <Shield className="w-4 h-4 text-green-400" />
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm text-neutral-400 mb-2">
                Similarity: {(moderation.similarity * 100).toFixed(1)}%
              </div>
              <div className="text-neutral-300 whitespace-pre-wrap mb-2">
                {moderation.content}
              </div>
              <div className="text-sm text-neutral-400">
                Previous flags: {moderation.flags.map(f => f.type).join(', ')}
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
} 